import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { InfoOutlined } from '@mui/icons-material';
import React, { useState, useEffect, useCallback } from 'react';

import { type SelectedJurisdictions } from '../../types';
import { createDefaultSelectedJurisdictions } from '../../utils';
import { JurisdictionFilterPopover } from '../JurisdictionFilterPopover';
import {
  MoreFiltersPopover,
  type MoreFiltersData,
} from '../MoreFiltersPopover';
import { SearchOperatorsPopover } from '../SearchOperatorsPopover';
import { TextFieldFilterPopover } from '../TextFieldFilterPopover';

import {
  FormContainer,
  SearchTypeChip,
  FilterChip,
  PillSearchBar,
  SearchInputField,
  InfoButton,
  SearchButton,
  MobileSearchButton,
  SearchRow,
  ProTipBox,
  FilterRow,
  FilterDivider,
  MobileActionsContainer,
  SearchTypeChipsContainer,
  TitleText,
  ProTipText,
  ProTipLabel,
  FilterLabel,
  ExactMatchCheckbox,
  ExactMatchLabel,
} from './components';

import type { TextFilterConfig, SearchFormProps } from './types';

// ============= COMPONENT =============

export const SearchForm: React.FC<SearchFormProps> = ({
  title,
  proTip,
  searchTypeOptions,
  filterChipConfigs = [],
  moreSectionConfig,
  onSubmit,
  initialSearchType,
  initialQuery = '',
  initialExactMatch = false,
  initialFilters,
  isLoading = false,
  disabled = false,
  showExactMatch = true,
  operatorsData,
  placeholder,
  testId = 'search-form',
  formContainerSx,
  titleSx,
}) => {
  const defaultSearchType =
    initialSearchType || searchTypeOptions[0]?.value || '';

  const [searchType, setSearchType] = useState<string>(defaultSearchType);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [exactMatchOnly, setExactMatchOnly] = useState(initialExactMatch);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [infoPopoverAnchor, setInfoPopoverAnchor] =
    useState<HTMLElement | null>(null);

  const [jurisdictions, setJurisdictions] = useState<SelectedJurisdictions>(
    initialFilters?.jurisdictions || createDefaultSelectedJurisdictions()
  );
  const [textFilters, setTextFilters] = useState<Record<string, string>>(
    initialFilters?.textFilters || {}
  );

  const [moreFilters, setMoreFilters] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    if (moreSectionConfig?.fields) {
      moreSectionConfig.fields.forEach((field) => {
        if (field.type === 'select' && field.defaultValue) {
          defaults[field.id] = field.defaultValue;
        }
      });
    }
    return {
      ...defaults,
      ...(initialFilters?.moreFilters || {}),
    };
  });

  const [filterPopoverAnchors, setFilterPopoverAnchors] = useState<
    Record<string, HTMLElement | null>
  >({});

  const textFilterConfigs = filterChipConfigs.filter(
    (config): config is TextFilterConfig => config.type === 'text'
  );

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
  };

  const isInfoPopoverOpen = Boolean(infoPopoverAnchor);

  const handleFilterChipClick = useCallback(
    (filterId: string, event: React.MouseEvent<HTMLElement>) => {
      if (!disabled && !isLoading) {
        setFilterPopoverAnchors((prev) => ({
          ...prev,
          [filterId]: prev[filterId] ? null : event.currentTarget,
        }));
      }
    },
    [disabled, isLoading]
  );

  const handleFilterPopoverClose = useCallback((filterId: string) => {
    setFilterPopoverAnchors((prev) => ({
      ...prev,
      [filterId]: null,
    }));
  }, []);

  const handleJurisdictionApply = useCallback(
    (selectedJurisdictions: SelectedJurisdictions) => {
      setJurisdictions(selectedJurisdictions);
      const hasSelected = Object.values(selectedJurisdictions).some(
        (isSelected) => isSelected === true
      );
      setActiveFilters((prev) =>
        hasSelected
          ? Array.from(new Set([...prev, 'jurisdiction']))
          : prev.filter((id) => id !== 'jurisdiction')
      );
      handleFilterPopoverClose('jurisdiction');
    },
    [handleFilterPopoverClose]
  );

  const handleTextFilterApply = useCallback(
    (filterId: string, value: string) => {
      setTextFilters((prev) => ({
        ...prev,
        [filterId]: value,
      }));
      setActiveFilters((prev) =>
        value
          ? Array.from(new Set([...prev, filterId]))
          : prev.filter((id) => id !== filterId)
      );
      handleFilterPopoverClose(filterId);
    },
    [handleFilterPopoverClose]
  );

  const handleMoreFiltersApply = useCallback(
    (filters: MoreFiltersData) => {
      setMoreFilters(filters);
      const moreId = moreSectionConfig?.id || 'more';
      const hasActiveFilters = Object.entries(filters).some(
        ([fieldId, value]) => {
          const fieldConfig = moreSectionConfig?.fields.find(
            (f) => f.id === fieldId
          );
          if (fieldConfig?.type === 'select') {
            return value !== (fieldConfig.defaultValue || '');
          }
          return value && value.length > 0;
        }
      );
      setActiveFilters((prev) =>
        hasActiveFilters
          ? Array.from(new Set([...prev, moreId]))
          : prev.filter((id) => id !== moreId)
      );
      handleFilterPopoverClose(moreId);
    },
    [handleFilterPopoverClose, moreSectionConfig]
  );

  useEffect(() => {
    if (!searchTypeOptions.find((opt) => opt.value === searchType)) {
      setSearchType(searchTypeOptions[0]?.value || '');
    }
  }, [searchTypeOptions, searchType]);

  // When user edits the query so it no longer matches the selected type's prefix, switch to Keyword
  useEffect(() => {
    const currentOption = searchTypeOptions.find(
      (opt) => opt.value === searchType
    );
    if (
      currentOption?.prefill &&
      !searchQuery
        .trim()
        .toLowerCase()
        .startsWith(currentOption.prefill.toLowerCase())
    ) {
      setSearchType(searchTypeOptions[0]?.value || '');
    }
  }, [searchQuery, searchType, searchTypeOptions]);

  useEffect(() => {
    setExactMatchOnly(initialExactMatch);
  }, [initialExactMatch]);

  const moreFiltersJson = initialFilters?.moreFilters
    ? JSON.stringify(initialFilters.moreFilters)
    : null;

  useEffect(() => {
    if (initialFilters?.moreFilters) {
      setMoreFilters((prev) => ({
        ...prev,
        ...initialFilters.moreFilters,
      }));
    }
  }, [moreFiltersJson]);

  const handleSearchTypeChange = (type: string) => {
    if (!disabled && !isLoading) {
      setSearchType(type);
      const selectedOption = searchTypeOptions.find(
        (opt) => opt.value === type
      );
      if (selectedOption?.prefill) {
        setSearchQuery(selectedOption.prefill);
      } else {
        setSearchQuery('');
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disabled && !isLoading && onSubmit) {
      onSubmit({
        searchType,
        searchQuery,
        exactMatchOnly,
        activeFilters,
        filters: {
          jurisdictions,
          textFilters,
          moreFilters,
        },
      });
    }
  };

  const currentSearchOption = searchTypeOptions.find(
    (opt) => opt.value === searchType
  );

  const isFormDisabled = disabled || isLoading;

  const getPlaceholder = () => {
    if (placeholder) {
      return placeholder;
    }
    return currentSearchOption?.placeholder || 'Search...';
  };

  const isFilterActive = (filterId: string): boolean => {
    const jurisdictionConfig = filterChipConfigs.find(
      (c) => c.type === 'jurisdiction'
    );
    if (jurisdictionConfig && filterId === jurisdictionConfig.id) {
      return Object.values(jurisdictions).some(
        (isSelected) => isSelected === true
      );
    }

    const textConfig = textFilterConfigs.find((c) => c.id === filterId);
    if (textConfig) {
      return (textFilters[filterId] || '').length > 0;
    }

    if (moreSectionConfig && filterId === moreSectionConfig.id) {
      return Object.entries(moreFilters).some(([fieldId, value]) => {
        const fieldConfig = moreSectionConfig.fields.find(
          (f) => f.id === fieldId
        );
        if (fieldConfig?.type === 'select') {
          return value !== (fieldConfig.defaultValue || '');
        }
        return value && value.length > 0;
      });
    }

    return activeFilters.includes(filterId);
  };

  return (
    <FormContainer data-testid={testId} sx={formContainerSx}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <TitleText variant="h5" sx={titleSx}>
          {title}
        </TitleText>

        {proTip && (
          <ProTipBox>
            <ProTipText variant="body2">
              <ProTipLabel>PRO TIP:</ProTipLabel> {proTip}
            </ProTipText>
          </ProTipBox>
        )}
      </Box>

      {/* Search Type Chips */}
      {searchTypeOptions.length > 0 && (
        <SearchTypeChipsContainer direction="row">
          {searchTypeOptions.map((option) => (
            <SearchTypeChip
              key={option.value}
              isSelected={searchType === option.value}
              onClick={() => handleSearchTypeChange(option.value)}
              variant={searchType === option.value ? 'contained' : 'outlined'}
              disabled={isFormDisabled}
            >
              {option.label}
            </SearchTypeChip>
          ))}
        </SearchTypeChipsContainer>
      )}

      {/* Search Form */}
      <form onSubmit={handleSubmit}>
        <SearchRow>
          <PillSearchBar>
            <SearchInputField
              id="searchQuery"
              type="text"
              name="searchQuery"
              placeholder={getPlaceholder()}
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              disabled={isFormDisabled}
              aria-label="Search query"
            />

            {operatorsData && (
              <>
                <InfoButton
                  size="small"
                  aria-label="Search help"
                  onClick={handleInfoClick}
                >
                  <InfoOutlined />
                </InfoButton>
                <SearchOperatorsPopover
                  anchorEl={infoPopoverAnchor}
                  open={isInfoPopoverOpen}
                  onClose={handleInfoClose}
                  operatorsData={operatorsData}
                />
              </>
            )}
          </PillSearchBar>

          <SearchButton
            type="submit"
            variant="contained"
            disabled={isFormDisabled}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </SearchButton>
        </SearchRow>

        {/* Add Search Details - Filter Chips */}
        {(filterChipConfigs.length > 0 ||
          moreSectionConfig ||
          showExactMatch) && (
          <FilterRow>
            {(filterChipConfigs.length > 0 || moreSectionConfig) && (
              <>
                <FilterLabel variant="caption">Add Search Details:</FilterLabel>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                  {filterChipConfigs.map((config) => (
                    <FilterChip
                      key={config.id}
                      isActive={isFilterActive(config.id)}
                      onClick={(e) => handleFilterChipClick(config.id, e)}
                      size="small"
                      disabled={isFormDisabled}
                    >
                      {config.label}
                    </FilterChip>
                  ))}
                  {moreSectionConfig && (
                    <FilterChip
                      key={moreSectionConfig.id}
                      isActive={isFilterActive(moreSectionConfig.id)}
                      onClick={(e) =>
                        handleFilterChipClick(moreSectionConfig.id, e)
                      }
                      size="small"
                      disabled={isFormDisabled}
                    >
                      {moreSectionConfig.label}
                    </FilterChip>
                  )}
                </Stack>
              </>
            )}

            {showExactMatch &&
              (filterChipConfigs.length > 0 || moreSectionConfig) && (
                <FilterDivider orientation="vertical" flexItem />
              )}

            {showExactMatch && (
              <ExactMatchLabel
                control={
                  <ExactMatchCheckbox
                    checked={exactMatchOnly}
                    onChange={(e) => setExactMatchOnly(e.target.checked)}
                    size="small"
                    disabled={isFormDisabled}
                  />
                }
                label="Exact Matches Only"
              />
            )}
          </FilterRow>
        )}

        {/* Filter Chip Popovers */}
        {filterChipConfigs.map((config) => {
          switch (config.type) {
            case 'jurisdiction':
              return (
                <JurisdictionFilterPopover
                  key={config.id}
                  anchorEl={filterPopoverAnchors[config.id] || null}
                  open={Boolean(filterPopoverAnchors[config.id])}
                  onClose={() => handleFilterPopoverClose(config.id)}
                  selectedJurisdictions={jurisdictions}
                  onApply={handleJurisdictionApply}
                />
              );
            case 'text':
              return (
                <TextFieldFilterPopover
                  key={config.id}
                  anchorEl={filterPopoverAnchors[config.id] || null}
                  open={Boolean(filterPopoverAnchors[config.id])}
                  onClose={() => handleFilterPopoverClose(config.id)}
                  title={config.label}
                  fieldLabel={
                    config.hint
                      ? `${config.fieldLabel} (${config.hint})`
                      : config.fieldLabel
                  }
                  placeholder={
                    config.placeholder ||
                    `Enter ${config.fieldLabel.toLowerCase()}`
                  }
                  currentValue={textFilters[config.id] || ''}
                  onApply={(value) => handleTextFilterApply(config.id, value)}
                  testId={`${config.id}-filter-popover`}
                />
              );
            default:
              return null;
          }
        })}

        {/* More Section Popover */}
        {moreSectionConfig && (
          <MoreFiltersPopover
            anchorEl={filterPopoverAnchors[moreSectionConfig.id] || null}
            open={Boolean(filterPopoverAnchors[moreSectionConfig.id])}
            onClose={() => handleFilterPopoverClose(moreSectionConfig.id)}
            config={moreSectionConfig}
            currentValues={moreFilters}
            onApply={handleMoreFiltersApply}
          />
        )}

        {/* Mobile Actions */}
        <MobileActionsContainer>
          <MobileSearchButton
            type="submit"
            variant="contained"
            disabled={isFormDisabled}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </MobileSearchButton>
        </MobileActionsContainer>
      </form>
    </FormContainer>
  );
};

export default SearchForm;
